import { useEffect, useRef, useState } from 'react'
import { Modal } from 'react-daisyui'
import { Button } from '@/components/Button.tsx'

interface FocalPointModalProps {
  modalRef: React.RefObject<HTMLDialogElement>
  imageUrl: string | null
  initialX?: number
  initialY?: number
  onConfirm: (centerX: number, centerY: number) => void
  isLoading?: boolean
}

export default function FocalPointModal({ modalRef, imageUrl, initialX = 0.5, initialY = 0.5, onConfirm, isLoading }: FocalPointModalProps) {
  const [point, setPoint] = useState({ x: initialX, y: initialY })
  const imgRef = useRef<HTMLImageElement>(null)

  // Reset point whenever imageUrl or initial values change (new file selected)
  useEffect(() => {
    setPoint({ x: initialX, y: initialY })
  }, [imageUrl, initialX, initialY])

  function handleImageClick(e: React.MouseEvent<HTMLImageElement>) {
    const rect = e.currentTarget.getBoundingClientRect()
    const x = (e.clientX - rect.left) / rect.width
    const y = (e.clientY - rect.top) / rect.height
    setPoint({ x: Math.max(0, Math.min(1, x)), y: Math.max(0, Math.min(1, y)) })
  }

  return (
    <Modal ref={modalRef} className="bg-neutral max-w-2xl">
      <Modal.Header>
        <span className="text-xl font-bold">Fokuspunkt setzen</span>
        <form method="dialog">
          <button className="btn btn-sm btn-circle btn-ghost absolute right-5 top-5">✕</button>
        </form>
      </Modal.Header>
      <Modal.Body>
        <div className="flex flex-col gap-4 items-center">
          <p className="text-sm opacity-70">Klicke auf das Bild, um den Fokuspunkt zu setzen.</p>
          {imageUrl && (
            <div className="relative inline-block cursor-crosshair">
              <img
                key={imageUrl}
                ref={imgRef}
                src={imageUrl}
                alt="Fokuspunkt setzen"
                className="object-contain max-h-[60vh] max-w-full"
                onClick={handleImageClick}
                draggable={false}
              />
              <div
                className="absolute w-6 h-6 border-2 border-primary rounded-full -translate-x-1/2 -translate-y-1/2 pointer-events-none shadow-[0_0_0_2px_rgba(0,0,0,0.3)]"
                style={{ left: `${point.x * 100}%`, top: `${point.y * 100}%` }}
              >
                <div className="absolute inset-1 bg-primary rounded-full opacity-50" />
              </div>
            </div>
          )}
          <div className="flex flex-row gap-3 justify-center">
            <form method="dialog">
              <Button color="ghost" disabled={isLoading}>Abbrechen</Button>
            </form>
            <Button
              color="primary"
              onClick={() => onConfirm(point.x, point.y)}
              loading={isLoading}
              disabled={isLoading}
            >
              Speichern
            </Button>
          </div>
        </div>
      </Modal.Body>
    </Modal>
  )
}
